import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { shared } from '../../styles/shared.js';
import { LocaleController } from '../../i18n/locale-controller.js';
import { generateRecognitionTrial, type RecognitionTrial } from '../../distractors/recognition.js';
import { getProgression } from '../../progression/context.js';
import { getAudioService } from '../../audio/context.js';
import { TRIALS_PER_SESSION, STREAK_THRESHOLD } from '../../session/config.js';
import type { SessionResult } from '../../progression/progression.js';
import '../../components/digit-tile.js';
import '../../components/rewards-overlay.js';
import '../../components/streak-celebration.js';
import '../../components/session-summary.js';

type Phase = 'playing' | 'summary';

const FEEDBACK_DELAY_MS = 1000;
/**
 * Hold the correct-answer highlight a beat past the last audio clip so the
 * toddler can connect the visual cue to what she just heard.
 */
const POST_AUDIO_HOLD_MS = 600;

@customElement('recognize-game')
export class RecognizeGame extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-6);
        height: 100%;
        padding: var(--space-5);
      }

      .target {
        font-family: var(--font-display);
        font-size: var(--type-l);
        text-align: center;
        margin: 0;
      }

      /*
       * Sustained outline on the correct tile during wrong-answer feedback.
       * The flash animation auto-clears after 250 ms, but this outline stays
       * the full feedback length so the toddler can connect tile + audio.
       */
      .tile-highlighted {
        outline: 6px solid var(--color-accent-lime);
        outline-offset: 4px;
        border-radius: var(--radius-lg);
      }

      .tiles {
        display: flex;
        gap: var(--space-5);
        justify-content: center;
        flex-wrap: wrap;
      }

      .progress {
        position: fixed;
        top: var(--space-3);
        right: var(--space-3);
        font-family: var(--font-display);
        font-weight: 700;
        font-size: var(--type-m);
        background: var(--color-surface);
        padding: var(--space-2) var(--space-4);
        border-radius: var(--radius-pill);
        box-shadow: var(--shadow-tile);
      }

      @media (max-height: 500px) {
        :host {
          gap: var(--space-3);
          padding: var(--space-3);
        }

        .target {
          font-size: var(--type-m);
        }

        .tiles {
          gap: var(--space-3);
        }

        .progress {
          font-size: var(--type-s);
          padding: var(--space-1) var(--space-3);
        }
      }
    `,
  ];

  @state() private phase: Phase = 'playing';
  @state() private trial: RecognitionTrial | null = null;
  @state() private trialIndex = 0;
  @state() private correctCount = 0;
  @state() private streak = 0;
  @state() private rewardShow = false;
  @state() private streakShow = false;
  @state() private locked = false;
  @state() private highlightCorrect = false;
  @state() private sessionResult: SessionResult | null = null;

  private locale = new LocaleController(this);

  override connectedCallback(): void {
    super.connectedCallback();
    void this.preloadSessionAudio();
    this.startNextTrial();
  }

  private async preloadSessionAudio(): Promise<void> {
    const params = getProgression().recognitionParams();
    const keys = [
      ...params.digits.map((d) => `digit.${d}`),
      'feedback.correct',
      'feedback.tryagain',
      'prompt.recognize',
    ];
    await getAudioService().preload(keys);
  }

  private startNextTrial(): void {
    const params = getProgression().recognitionParams();
    this.trial = generateRecognitionTrial(params);
    this.locked = false;
    this.highlightCorrect = false;
    void getAudioService().play(`digit.${this.trial.target}`, 'prompt');
  }

  private async onTileTap(value: number): Promise<void> {
    if (this.locked || this.trial === null) return;
    this.locked = true;

    const isCorrect = value === this.trial.target;
    const audio = getAudioService();
    const progression = getProgression();
    const level = progression.getLevel('recognition');

    await progression.recordAttempt('recognition', level, isCorrect);

    if (isCorrect) {
      this.correctCount++;
      this.streak++;
      void audio.play('feedback.correct', 'reward');
      this.rewardShow = true;
      if (this.streak >= STREAK_THRESHOLD) {
        this.streakShow = true;
      }
      window.setTimeout(() => {
        this.advance();
      }, FEEDBACK_DELAY_MS);
    } else {
      this.streak = 0;
      this.highlightCorrect = true;
      // Wait for the demo audio to actually finish before moving on, so the
      // toddler has time to connect "let's try again" + the spoken digit
      // with the visually-highlighted correct tile.
      await audio.play('feedback.tryagain', 'reward');
      await audio.play(`digit.${this.trial.target}`, 'reward');
      window.setTimeout(() => {
        this.advance();
      }, POST_AUDIO_HOLD_MS);
    }
  }

  private advance(): void {
    this.trialIndex++;
    if (this.trialIndex >= TRIALS_PER_SESSION) {
      void this.finishSession();
      return;
    }
    this.startNextTrial();
  }

  private async finishSession(): Promise<void> {
    const result = await getProgression().recordSessionResult('recognition', this.correctCount);
    this.sessionResult = result;
    this.phase = 'summary';
  }

  private onRewardDone(): void {
    this.rewardShow = false;
  }

  private onStreakDone(): void {
    this.streakShow = false;
  }

  override render() {
    if (this.phase === 'summary' && this.sessionResult) {
      return html`<session-summary
        .starsEarned=${this.correctCount}
        .promoted=${this.sessionResult.promoted}
        .demoted=${this.sessionResult.demoted}
      ></session-summary>`;
    }

    if (this.trial === null) return html``;

    return html`<div class="progress" data-testid="progress">
        ${this.trialIndex + 1} / ${TRIALS_PER_SESSION}
      </div>
      <h2 class="target">
        ${this.locale.locale === 'nl' ? 'Welk cijfer is dit?' : 'Which number is this?'}
      </h2>
      <div class="tiles">
        ${this.trial.tiles.map((v) => {
          const isTargetHighlight = this.highlightCorrect && v === this.trial?.target;
          return html`<digit-tile
            variant="digit"
            .value=${v}
            ?disabled=${this.locked && !isTargetHighlight}
            ?flash=${isTargetHighlight}
            class=${isTargetHighlight ? 'tile-highlighted' : ''}
            data-testid="recognize-tile"
            data-correct=${v === this.trial?.target ? 'true' : 'false'}
            @tile-tap=${(e: CustomEvent<{ value: number | null }>) =>
              this.onTileTap(e.detail.value!)}
          ></digit-tile>`;
        })}
      </div>
      <rewards-overlay .show=${this.rewardShow} @reward-done=${this.onRewardDone}></rewards-overlay>
      <streak-celebration
        .show=${this.streakShow}
        @streak-done=${this.onStreakDone}
      ></streak-celebration>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'recognize-game': RecognizeGame;
  }
}
